import { useEffect, useCallback } from 'react';
import { useLocation } from '../contexts/LocationContext';

/**
 * Custom hook for simplified location operations
 * @param {Object} options - Configuration options
 * @param {boolean} options.autoRequest - Automatically request location on mount
 * @param {boolean} options.watch - Enable location watching
 * @param {Object} options.geoOptions - Geolocation API options
 */
export const useLocationServices = (options = {}) => {
    const {
        autoRequest = false,
        watch = false,
        geoOptions = {}
    } = options;

    const {
        location,
        isLoading,
        error,
        permissionStatus,
        isSupported,
        isWatching,
        getCurrentLocation,
        watchLocation,
        stopWatching,
        clearLocation,
        calculateDistance,
        getAddressFromCoords
    } = useLocation();

    // Auto-request location on mount if enabled
    useEffect(() => {
        if (autoRequest && isSupported && permissionStatus !== 'denied') {
            if (watch) {
                watchLocation(geoOptions);
            } else {
                getCurrentLocation(geoOptions);
            }
        }
    }, [autoRequest, watch, isSupported, permissionStatus, geoOptions, watchLocation, getCurrentLocation]);

    // Request location with error handling
    const requestLocation = useCallback(async (requestOptions = {}) => {
        try {
            const location = await getCurrentLocation({ ...geoOptions, ...requestOptions });
            return { success: true, location };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }, [getCurrentLocation, geoOptions]);

    // Start watching with error handling
    const startWatching = useCallback((watchOptions = {}) => {
        try {
            const watchId = watchLocation({ ...geoOptions, ...watchOptions });
            return { success: true, watchId };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }, [watchLocation, geoOptions]);

    // Check if user is within a certain radius of a point
    const isWithinRadius = useCallback((targetLat, targetLon, radiusKm) => {
        if (!location) return null;

        const distance = calculateDistance(
            location.latitude,
            location.longitude,
            targetLat,
            targetLon
        );

        return distance <= radiusKm;
    }, [location, calculateDistance]);

    // Get location status summary
    const getLocationStatus = useCallback(() => {
        if (!isSupported) return 'unsupported';
        if (permissionStatus === 'denied') return 'denied';
        if (isLoading) return 'loading';
        if (error) return 'error';
        if (location) return 'available';
        return 'unknown';
    }, [isSupported, permissionStatus, isLoading, error, location]);

    // Format location for display
    const formatLocation = useCallback((precision = 4) => {
        if (!location) return null;

        return {
            coordinates: `${location.latitude.toFixed(precision)}, ${location.longitude.toFixed(precision)}`,
            accuracy: location.accuracy ? `±${Math.round(location.accuracy)}m` : 'Unknown',
            timestamp: location.timestamp ? new Date(location.timestamp).toLocaleString() : null
        };
    }, [location]);

    return {
        // State
        location,
        isLoading,
        error,
        permissionStatus,
        isSupported,
        isWatching,
        status: getLocationStatus(),

        // Formatted data
        formattedLocation: formatLocation(),

        // Actions
        requestLocation,
        startWatching,
        stopWatching,
        clearLocation,

        // Utilities
        isWithinRadius,
        calculateDistance,
        getAddressFromCoords,
        formatLocation
    };
};